import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@careandkey/shared';
import { supabase } from '../lib/supabase';

const CADENCE = ['weekly', 'biweekly', 'monthly', 'bimonthly', 'quarterly', 'annual'] as const;
const ANCHOR = ['contract_start', 'calendar'] as const;
const VISIT_OVERAGE = ['all_or_nothing', 'prorata'] as const;
const MINUTE_OVERAGE = ['prorata', 'round_quarter_hour', 'all_or_nothing'] as const;
const MONTH_END = ['last_day', 'same_day_next_month'] as const;

type Cfg = {
  allowance_cadence: string | null; allowance_visits_per_period: number | null; allowance_minutes_per_period: number | null;
  allowance_carryover: boolean | null; allowance_carryover_cap: number | null; allowance_anchor: string | null;
  allowance_visit_overage: string | null; allowance_minute_overage: string | null; allowance_month_end: string | null;
  allowance_overage_rate_id: string | null;
};
type Change = { id: number; field: string; old_value: string | null; new_value: string | null; changed_at: string };

// Allowance parameter editor. kind='offer' writes set_offer_allowance (org_admin);
// kind='contract' writes set_contract_allowance (each field nullable = inherit).
export function AllowanceEditor({ kind, id, name, onClose, onSaved }: { kind: 'offer' | 'contract'; id: string; name: string; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [rates, setRates] = useState<{ id: string; name: string }[]>([]);
  const [history, setHistory] = useState<Change[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const table = kind === 'offer' ? 'catalogue_offers' : 'contracts';

  useEffect(() => {
    (async () => {
      const cols = 'allowance_cadence,allowance_visits_per_period,allowance_minutes_per_period,allowance_carryover,allowance_carryover_cap,allowance_anchor,allowance_visit_overage,allowance_minute_overage,allowance_month_end,allowance_overage_rate_id';
      const [c, r, h] = await Promise.all([
        supabase.from(table).select(cols).eq('id', id).single(),
        supabase.from('labour_rates').select('id,name').is('deleted_at', null).order('name'),
        supabase.from('allowance_setting_changes').select('id,field,old_value,new_value,changed_at').eq('entity_type', kind).eq('entity_id', id).order('changed_at', { ascending: false }).limit(12),
      ]);
      setCfg(c.data as unknown as Cfg);
      setRates((r.data as any[]) ?? []);
      setHistory((h.data as Change[]) ?? []);
    })();
  }, [id, kind, table]);

  if (!cfg) return null;
  const set = (patch: Partial<Cfg>) => setCfg({ ...cfg, ...patch });
  const inherit = kind === 'contract'; // contract params can be null = inherit from offer
  const numOrNull = (v: string) => (v.trim() === '' ? null : Math.max(0, Number(v)));

  async function save() {
    setBusy(true); setErr('');
    const fn = kind === 'offer' ? 'set_offer_allowance' : 'set_contract_allowance';
    const key = kind === 'offer' ? 'p_offer' : 'p_contract';
    const { error } = await supabase.rpc(fn, {
      [key]: id, p_cadence: cfg!.allowance_cadence, p_visits: cfg!.allowance_visits_per_period, p_minutes: cfg!.allowance_minutes_per_period,
      p_carryover: kind === 'offer' ? (cfg!.allowance_carryover ?? false) : cfg!.allowance_carryover, p_carryover_cap: cfg!.allowance_carryover_cap, p_anchor: cfg!.allowance_anchor,
      p_visit_overage: cfg!.allowance_visit_overage, p_minute_overage: cfg!.allowance_minute_overage, p_month_end: cfg!.allowance_month_end, p_overage_rate: cfg!.allowance_overage_rate_id,
    } as any);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved(); onClose();
  }

  const Field = ({ label, children, overridden }: { label: string; children: React.ReactNode; overridden?: boolean }) => (
    <label className="flex flex-col gap-1 text-sm">
      <span className="flex items-center gap-1.5 text-muted">{label}{inherit && <span className={`rounded-pill px-1.5 py-0.5 text-2xs ${overridden ? 'bg-accent/10 text-accent' : 'bg-bg text-muted'}`}>{overridden ? t('allow.overridden') : t('allow.inherited')}</span>}</span>
      {children}
    </label>
  );
  const sel = 'h-9 rounded-md border border-border bg-card px-2.5 text-sm';
  const inp = 'h-9 w-full rounded-md border border-border bg-card px-2.5 text-sm outline-none';
  const inheritOpt = inherit ? <option value="">{t('allow.inherit')}</option> : null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <Card className="my-6 w-full max-w-[560px] p-5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <h3 className="mb-0.5 text-base font-semibold">{t('allow.title')}</h3>
        <p className="mb-4 text-sm text-muted">{name}</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('allow.cadence')} overridden={!!cfg.allowance_cadence}>
            <select className={sel} value={cfg.allowance_cadence ?? ''} onChange={(e) => set({ allowance_cadence: e.target.value || null })}>
              {inheritOpt}{CADENCE.map((c) => <option key={c} value={c}>{t('allow.cad_' + c)}</option>)}
            </select>
          </Field>
          <Field label={t('allow.anchor')} overridden={!!cfg.allowance_anchor}>
            <select className={sel} value={cfg.allowance_anchor ?? ''} onChange={(e) => set({ allowance_anchor: e.target.value || null })}>
              {inheritOpt}{ANCHOR.map((a) => <option key={a} value={a}>{t('allow.anc_' + a)}</option>)}
            </select>
          </Field>
          <Field label={t('allow.visits')} overridden={cfg.allowance_visits_per_period != null}>
            <input type="number" min={0} className={inp} value={cfg.allowance_visits_per_period ?? ''} onChange={(e) => set({ allowance_visits_per_period: numOrNull(e.target.value) })} />
          </Field>
          <Field label={t('allow.minutes')} overridden={cfg.allowance_minutes_per_period != null}>
            <input type="number" min={0} className={inp} value={cfg.allowance_minutes_per_period ?? ''} onChange={(e) => set({ allowance_minutes_per_period: numOrNull(e.target.value) })} />
          </Field>
          <Field label={t('allow.visitOverage')} overridden={!!cfg.allowance_visit_overage}>
            <select className={sel} value={cfg.allowance_visit_overage ?? ''} onChange={(e) => set({ allowance_visit_overage: e.target.value || null })}>
              {inheritOpt}{VISIT_OVERAGE.map((o) => <option key={o} value={o}>{t('allow.ov_' + o)}</option>)}
            </select>
          </Field>
          <Field label={t('allow.minuteOverage')} overridden={!!cfg.allowance_minute_overage}>
            <select className={sel} value={cfg.allowance_minute_overage ?? ''} onChange={(e) => set({ allowance_minute_overage: e.target.value || null })}>
              {inheritOpt}{MINUTE_OVERAGE.map((o) => <option key={o} value={o}>{t('allow.ov_' + o)}</option>)}
            </select>
          </Field>
          <Field label={t('allow.carryover')} overridden={cfg.allowance_carryover != null}>
            <select className={sel} value={cfg.allowance_carryover == null ? '' : String(cfg.allowance_carryover)} onChange={(e) => set({ allowance_carryover: e.target.value === '' ? null : e.target.value === 'true' })}>
              {inheritOpt}<option value="false">{t('common.no')}</option><option value="true">{t('common.yes')}</option>
            </select>
          </Field>
          <Field label={t('allow.carryoverCap')} overridden={cfg.allowance_carryover_cap != null}>
            <input type="number" min={0} className={inp} placeholder={t('allow.noCap')} value={cfg.allowance_carryover_cap ?? ''} onChange={(e) => set({ allowance_carryover_cap: numOrNull(e.target.value) })} />
          </Field>
          <Field label={t('allow.monthEnd')} overridden={!!cfg.allowance_month_end}>
            <select className={sel} value={cfg.allowance_month_end ?? ''} onChange={(e) => set({ allowance_month_end: e.target.value || null })}>
              {inheritOpt}{MONTH_END.map((m) => <option key={m} value={m}>{t('allow.me_' + m)}</option>)}
            </select>
          </Field>
          <Field label={t('allow.overageRate')} overridden={!!cfg.allowance_overage_rate_id}>
            <select className={sel} value={cfg.allowance_overage_rate_id ?? ''} onChange={(e) => set({ allowance_overage_rate_id: e.target.value || null })}>
              <option value="">{inherit ? t('allow.inherit') : t('allow.orgDefault')}</option>{rates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
        </div>
        {err && <div className="mt-3 rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <p className="mt-3 text-xs text-muted">{t('allow.nextPeriodNote')}</p>

        {history.length > 0 && (
          <details className="mt-3"><summary className="cursor-pointer text-sm font-medium">{t('allow.history')} ({history.length})</summary>
            <div className="mt-2 flex flex-col gap-1 text-xs text-muted">
              {history.map((h) => <div key={h.id}>{t('allow.f_' + h.field, h.field)}: <span className="font-mono">{h.old_value ?? '—'}</span> → <span className="font-mono">{h.new_value ?? '—'}</span></div>)}
            </div>
          </details>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="accent" disabled={busy} onClick={save}>{t('common.save')}</Button>
        </div>
      </Card>
    </div>
  );
}
